import { Skeleton } from '@shared/components/Skeleton';
import type { FlexProps } from '@withjoy/joykit';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import type { Property } from 'csstype';
import type { ReactNode } from 'react';
import React from 'react';
import type { ResponsiveValue } from 'styled-system';
import { CheckFilled, Circle } from '@withjoy/joykit/icons';

interface ChoiceGroupProps<Value> {
  title?: string;
  subTitle?: string;
  value: Value;
  price?: string;
  choices?: Value[];
  columns?: number;
  minHeight?: number;
  setValue?: (value: Value) => void;
  getName?: (value: Value) => string;
  getOpacity?: (value: Value) => number;
  children?: ReactNode;
  /** Slot between the title row and the choices, for a control that applies to the group as a whole. */
  afterTitle?: ReactNode;
  /**
   * Slot in the top-right corner of a single choice, for a mark that describes that choice rather
   * than the group -- the premium diamond, today. Returning nothing leaves the choice unmarked.
   */
  renderChoiceBadge?: (value: Value) => ReactNode;
  /**
   * Whether a single choice is listed but cannot be picked. Rendered with `aria-disabled` rather
   * than the native attribute on purpose: `disabled` takes the choice out of the tab order, and a
   * choice is only ever disabled here when there is a reason worth reading beside it -- which a
   * keyboard or screen-reader user could then never reach. Pair it with `getChoiceDescribedBy`.
   */
  isChoiceDisabled?: (value: Value) => boolean;
  /** Id of the element explaining a choice, for `aria-describedby`. */
  getChoiceDescribedBy?: (value: Value) => string | undefined;
  hideValueFromTitle?: boolean;
  textTransform?: ResponsiveValue<Property.TextTransform>;
  icon?: ReactNode;
  direction?: 'row' | 'column';
}

const Root: React.FC = ({ children }) => {
  return (
    <Flex
      rowGap={3}
      flexDirection="column"
      width="100%"
    >
      {children}
    </Flex>
  );
};

const Eyebrow: React.FC<Pick<FlexProps, 'height' | 'alignItems'>> = ({ children, height, alignItems }) => {
  return (
    <Flex
      columnGap={4}
      height={height}
      alignItems={alignItems}
    >
      {children}
    </Flex>
  );
};

const ChoicesContainer: React.FC<{ columns?: number; direction?: 'row' | 'column' }> = ({ children, columns, direction = 'row' }) => {
  return direction === 'column' ? (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      rowGap={5}
    >
      {children}
    </Flex>
  ) : (
    <Box
      display="grid"
      gridTemplateColumns={`repeat(${columns ?? 2}, 1fr)`}
      columnGap={3}
      rowGap={5}
      width="100%"
    >
      {children}
    </Box>
  );
};

export function ChoiceGroup<Value>(props: ChoiceGroupProps<Value>) {
  const {
    title,
    subTitle,
    value,
    hideValueFromTitle,
    price,
    choices,
    columns,
    minHeight,
    setValue,
    getName,
    getOpacity,
    children: originalChildren,
    afterTitle,
    renderChoiceBadge,
    isChoiceDisabled,
    getChoiceDescribedBy,
    textTransform,
    icon,
    direction = 'row',
  } = props;

  const children = Array.isArray(originalChildren) ? originalChildren.filter(el => el) : originalChildren;

  return (
    <Root>
      <Eyebrow alignItems={icon ? 'center' : undefined}>
        {title && (
          <>
            <TextV2 typographyVariant="hed1">{title}</TextV2>
            {!hideValueFromTitle && (
              <TextV2
                typographyVariant="body1"
                color="mono12"
                textTransform={textTransform ?? 'capitalize'}
              >
                {getName?.(value) ?? value}
              </TextV2>
            )}
            {icon}
          </>
        )}
        {price && (
          <TextV2
            typographyVariant="hed1"
            color="mono12"
            marginLeft="auto"
          >
            {price}
          </TextV2>
        )}
      </Eyebrow>
      {subTitle && (
        <TextV2
          typographyVariant="body1"
          color="mono14"
          marginBottom={2}
        >
          {subTitle}
        </TextV2>
      )}
      {afterTitle}
      {(() => {
        if (!choices) return children; // In cases such as a Select/Dropdown.

        // SizeChoiceGroup is an example where we dynamically query for choices.
        // It is possible to end up with only one choice. If one choice, then we hide.
        if (choices.length <= 1) return null;

        // In cases where the choices are text or images.
        return (
          <ChoicesContainer
            columns={columns}
            direction={direction}
          >
            {choices.map((choice, i) => {
              const choiceVisual = Array.isArray(children) ? children[i] : undefined;
              const choiceBadge = renderChoiceBadge?.(choice);
              const isDisabled = isChoiceDisabled?.(choice) ?? false;
              return (
                <ButtonV2
                  key={i}
                  aria-disabled={isDisabled || undefined}
                  aria-describedby={getChoiceDescribedBy?.(choice)}
                  // So a badge can sit in the choice's own corner. The badge is rendered inside the
                  // button rather than around it because the button is what draws the frame.
                  position="relative"
                  alignItems="center"
                  justifyContent={direction === 'column' ? 'flex-start' : 'center'}
                  borderColor={value === choice ? 'mono14' : 'mono5'}
                  borderWidth={value === choice ? '2px' : '1px'}
                  borderRadius={8}
                  cursor={isDisabled ? 'not-allowed' : 'pointer'}
                  flex={0}
                  height="unset"
                  intent="neutral"
                  minHeight={minHeight ?? 98}
                  onClick={() => {
                    if (isDisabled) return;
                    setValue?.(choice);
                  }}
                  opacity={getOpacity?.(choice) ?? 1}
                  padding={direction === 'column' ? 5 : 2}
                  variant="outline"
                  whiteSpace="normal" // We often have content that is multi-line, with limited width
                  _active={{
                    backgroundColor: 'transparent',
                  }}
                  _hover={{
                    backgroundColor: 'transparent',
                  }}
                  fullWidth={direction === 'column'}
                  startIcon={
                    direction === 'column' ? (
                      value === choice ? (
                        <CheckFilled
                          size="md"
                          marginRight={2}
                        />
                      ) : (
                        <Circle
                          size="md"
                          fill="white"
                          marginRight={2}
                        />
                      )
                    ) : undefined
                  }
                >
                  {choiceBadge && (
                    <Box
                      position="absolute"
                      top={2}
                      right={2}
                      lineHeight={0}
                      // Load-bearing. The badge is emitted before `choiceVisual`, so a visual that
                      // is itself positioned or transformed paints in the same stacking layer and,
                      // as the later sibling, covers it -- the mark renders and is then hidden,
                      // which looks exactly like the badge never being asked for.
                      zIndex={1}
                    >
                      {choiceBadge}
                    </Box>
                  )}
                  {choiceVisual ? (
                    choiceVisual
                  ) : (
                    <TextV2
                      key={i}
                      typographyVariant="body1"
                      color="mono12"
                      textTransform="capitalize"
                    >
                      {getName?.(choice) ?? choice}
                    </TextV2>
                  )}
                </ButtonV2>
              );
            })}
          </ChoicesContainer>
        );
      })()}
    </Root>
  );
}

const ChoicesSkeleton = React.memo(({ minHeight = 98, choices }: { minHeight?: number; choices: number }) => {
  return (
    <>
      {Array.from({ length: choices }).map((_, index) => (
        <Skeleton
          borderRadius={8}
          key={index}
          minHeight={minHeight}
        />
      ))}
    </>
  );
});

ChoiceGroup.Skeleton = ({
  choices = 0,
  columns,
  disablePriceSkeleton,
  minHeight,
}: Pick<ChoiceGroupProps<unknown>, 'columns' | 'minHeight'> & { disablePriceSkeleton?: boolean; choices?: number }) => {
  return (
    <Root>
      <Eyebrow height={20}>
        <Skeleton width={80} />
        <Skeleton width={60} />
        {!disablePriceSkeleton && (
          <Skeleton
            width={70}
            marginLeft="auto"
          />
        )}
      </Eyebrow>
      {choices > 0 && (
        <ChoicesContainer columns={columns}>
          <ChoicesSkeleton
            minHeight={minHeight}
            choices={choices}
          />
        </ChoicesContainer>
      )}
    </Root>
  );
};
